import type {
  AgentProvider,
  BareFsChange,
  BoardAgentActionMap,
  BoardAgentEventMap,
  BoardUpdatePayload,
  CardAILogEntry,
  CardAISessionFull,
  CardAISessionSummary,
  ConfigResponse,
  RunnerActivity,
  SetConfigPayload,
  SimulatorDevice,
  SimulatorInput,
  TerminalServerMessage
} from '@shared/all/domains/electron';
import { chunkWire, makeChunkReassembler } from '@shared/all/helpers/wireChunk';

import { noteRunnerAction } from './secretsWrites';

export type DataChannelInterface = {
  send: (msg: string) => void;
  onMessage: (handler: (msg: string) => void) => () => void;
  // Fires when this channel can no longer carry a reply (peer closed, failed,
  // or torn down). Without it, an in-flight request on a NO_TIMEOUT_ACTIONS
  // action waits forever for a result that can never arrive. Optional so the
  // electron/local transports need no close notion.
  onClose?: (handler: () => void) => () => void;
};

export type TerminalHandle = {
  write: (data: string) => void;
  onData: (cb: (chunk: Uint8Array) => void) => () => void;
  /** Output as the runner sent it: base64, not decoded. For a consumer that
   *  hands it on encoded (mobile's WebView), so a burst is not decoded and
   *  encoded again. Absent on a transport that has no wire form. */
  onBase64?: (cb: (b64: string) => void) => () => void;
  onExit: (
    cb: (info: { code: number | null; reason?: string }) => void
  ) => () => void;
  resize: (cols: number, rows: number) => void;
  close: () => void;
};

export type OpenTerminal = (params: {
  postId: string;
  cols: number;
  rows: number;
  /** Stable tab key - picks which tmux session inside the container we
   *  attach to. Same key across reopens = same persistent shell. */
  terminalKey: string;
  /** Boot this tab into the card's agent conversation instead of a bare
   *  prompt. Only applies when the tmux session is created; reopening
   *  reattaches to the running agent. */
  resumeAgentSessionId?: string;
  resumeProvider?: AgentProvider;
  /** Start a NEW agent on this card when there's nothing to resume; ignored
   *  when `resumeAgentSessionId` is set. The card itself is not sent - the
   *  agent reads it over the factiii-board MCP. */
  briefCard?: boolean;
}) => TerminalHandle;

/** Destroy the underlying tmux session entirely (used for explicit tab
 *  close from the UI). Plain `handle.close()` just detaches. */
export type KillTerminal = (params: {
  postId: string;
  terminalKey: string;
}) => void;

/** Restart what the tab runs without losing the tab. Fire-and-forget like
 *  KillTerminal: the runner respawns the pane and the attached stream simply
 *  shows a fresh prompt. */
export type RestartTerminal = (params: {
  postId: string;
  terminalKey: string;
}) => void;

export type BoardAgentAPI = {
  getConfig: () => Promise<ConfigResponse>;
  setConfig: (
    config: SetConfigPayload
  ) => Promise<BoardAgentActionMap['setConfig']['result']>;
  clearToken: (
    key: BoardAgentActionMap['clearToken']['payload']['key']
  ) => Promise<BoardAgentActionMap['clearToken']['result']>;

  askBoard: (
    params: BoardAgentActionMap['ask']['payload']
  ) => Promise<BoardAgentActionMap['ask']['result']>;
  resetBoardSession: () => Promise<void>;
  /** One Ask conversation's transcript. No id means the most recently used. */
  boardSession: (
    conversationId?: string
  ) => Promise<BoardAgentActionMap['boardSession']['result']>;
  /** Every Ask conversation of the space. Rejects on a runner older than
   *  conversations, which has one thread and no list. */
  boardSessions: () => Promise<BoardAgentActionMap['boardSessions']['result']>;
  /** Rename, label, or set aside / bring back a conversation. */
  boardSessionUpdate: (
    params: BoardAgentActionMap['boardSessionUpdate']['payload']
  ) => Promise<void>;
  boardSessionDelete: (conversationId: string) => Promise<void>;
  /** Hand an Ask conversation to a terminal: stop the ask agent, return the
   *  resume id + provider for a board-key terminal. */
  boardHandoff: (
    conversationId?: string
  ) => Promise<BoardAgentActionMap['boardHandoff']['result']>;

  /** Skills modal: every skill both CLIs can discover, global and repo, with
   *  each repo skill's merge state. */
  skillsList: () => Promise<BoardAgentActionMap['skillsList']['result']>;
  skillRead: (
    params: BoardAgentActionMap['skillRead']['payload']
  ) => Promise<BoardAgentActionMap['skillRead']['result']>;
  /** Writes the host's own skill dir: applies at once, to every space. */
  skillWriteGlobal: (
    params: BoardAgentActionMap['skillWriteGlobal']['payload']
  ) => Promise<void>;
  skillDeleteGlobal: (
    params: BoardAgentActionMap['skillDeleteGlobal']['payload']
  ) => Promise<void>;
  /** Pushes the edit to a `skill/*` branch; merging it is the approval gate. */
  skillProposeRepo: (
    params: BoardAgentActionMap['skillProposeRepo']['payload']
  ) => Promise<BoardAgentActionMap['skillProposeRepo']['result']>;
  skillMigrate: (
    params: BoardAgentActionMap['skillMigrate']['payload']
  ) => Promise<BoardAgentActionMap['skillMigrate']['result']>;

  /** Deploy button entry: is the deploy contract on the default branch? */
  deployStatus: () => Promise<BoardAgentActionMap['deployStatus']['result']>;
  /** Branches on origin the release picker can offer, newest first. */
  deployBranches: () => Promise<
    BoardAgentActionMap['deployBranches']['result']
  >;
  /** Skill creator: drafts into the workspace for preview; streams via
   *  onBoardLog. Nothing is pushed until deployApproveSkill. */
  deployCreateSkill: (
    params: BoardAgentActionMap['deployCreateSkill']['payload']
  ) => Promise<BoardAgentActionMap['deployCreateSkill']['result']>;
  deployReviseSkill: (
    params: BoardAgentActionMap['deployReviseSkill']['payload']
  ) => Promise<BoardAgentActionMap['deployReviseSkill']['result']>;
  /** Resume a pushed deploy-skill branch (committed as-is) into review. */
  deployLoadSkillBranch: (
    params: BoardAgentActionMap['deployLoadSkillBranch']['payload']
  ) => Promise<BoardAgentActionMap['deployLoadSkillBranch']['result']>;
  /** In-flight creator state, for reattaching a reopened modal. */
  deployCreatorState: () => Promise<
    BoardAgentActionMap['deployCreatorState']['result']
  >;
  /** Run the merged environment checker: read-only "good to push" preflight. */
  deployCheckEnvironment: () => Promise<
    BoardAgentActionMap['deployCheckEnvironment']['result']
  >;
  /** Check failed -> AI installs what's missing (env only), then re-checks. */
  deployFixEnvironment: (
    params: BoardAgentActionMap['deployFixEnvironment']['payload']
  ) => Promise<BoardAgentActionMap['deployFixEnvironment']['result']>;
  /** In-flight/settled env check+fix state, for reattaching a reopened modal. */
  deployEnvState: () => Promise<
    BoardAgentActionMap['deployEnvState']['result']
  >;
  /** Env check/fix settled - refetch deployEnvState on it. */
  onDeployEnv: (
    callback: (data: BoardAgentEventMap['deploy-env']) => void
  ) => () => void;
  /** Deploy run: start fires and returns; progress via onDeployRun + state. */
  deployRunStart: (
    params: BoardAgentActionMap['deployRunStart']['payload']
  ) => Promise<BoardAgentActionMap['deployRunStart']['result']>;
  deployRunState: () => Promise<
    BoardAgentActionMap['deployRunState']['result']
  >;
  deployRunAnswer: (
    params: BoardAgentActionMap['deployRunAnswer']['payload']
  ) => Promise<BoardAgentActionMap['deployRunAnswer']['result']>;
  /** Password goes runner-direct; it never enters the agent session. */
  setupAuthorize: (
    params: BoardAgentActionMap['setupAuthorize']['payload']
  ) => Promise<BoardAgentActionMap['setupAuthorize']['result']>;
  /** Password goes runner-direct; it never enters the agent session. */
  deployRunAuthorize: (
    params: BoardAgentActionMap['deployRunAuthorize']['payload']
  ) => Promise<BoardAgentActionMap['deployRunAuthorize']['result']>;
  /** The one command in this space waiting on the store password, whatever
   *  session ran it. Null when nothing is waiting. */
  secretsAuthState: () => Promise<
    BoardAgentActionMap['secretsAuthState']['result']
  >;
  secretsAuthAnswer: (
    params: BoardAgentActionMap['secretsAuthAnswer']['payload']
  ) => Promise<BoardAgentActionMap['secretsAuthAnswer']['result']>;
  onSecretsAuth: (
    callback: (payload: BoardAgentEventMap['secrets-auth']) => void
  ) => () => void;
  /** Owner only: members' dangerous job calls waiting on the owner. */
  dangerousApprovalState: () => Promise<
    BoardAgentActionMap['dangerousApprovalState']['result']
  >;
  /** Owner only: approve (this one call) or deny a waiting call. */
  dangerousApprovalAnswer: (
    params: BoardAgentActionMap['dangerousApprovalAnswer']['payload']
  ) => Promise<BoardAgentActionMap['dangerousApprovalAnswer']['result']>;
  /** End the unlock window now: the runner wipes the age key from memory. */
  secretsLock: () => Promise<BoardAgentActionMap['secretsLock']['result']>;
  /** Owner only. Re-wraps the age key; no secret value changes. */
  secretsExportBackup: (
    params: BoardAgentActionMap['secretsExportBackup']['payload']
  ) => Promise<BoardAgentActionMap['secretsExportBackup']['result']>;
  /** Owner only, direct link only. The result carries the backup's values. */
  secretsOpenBackup: (
    params: BoardAgentActionMap['secretsOpenBackup']['payload']
  ) => Promise<BoardAgentActionMap['secretsOpenBackup']['result']>;
  /** Owner only, direct link only. Masked tails, never a value. */
  secretsImportBackupPlan: (
    params: BoardAgentActionMap['secretsImportBackupPlan']['payload']
  ) => Promise<BoardAgentActionMap['secretsImportBackupPlan']['result']>;
  secretsImportBackupApply: (
    params: BoardAgentActionMap['secretsImportBackupApply']['payload']
  ) => Promise<BoardAgentActionMap['secretsImportBackupApply']['result']>;
  secretsChangePassword: (
    params: BoardAgentActionMap['secretsChangePassword']['payload']
  ) => Promise<BoardAgentActionMap['secretsChangePassword']['result']>;
  /** What each board member gets: one level, None to Screens. Any channel
   *  reads its own; only the owner sets one. */
  runnerAccessGet: () => Promise<
    BoardAgentActionMap['runnerAccessGet']['result']
  >;
  runnerAccessSet: (
    params: BoardAgentActionMap['runnerAccessSet']['payload']
  ) => Promise<BoardAgentActionMap['runnerAccessSet']['result']>;
  /** The owner changed this member's level on this board. */
  onRunnerAccess: (
    callback: (payload: BoardAgentEventMap['runner-access']) => void
  ) => () => void;
  /** The jobs the default branch declares. A call names a job, never a
   *  command. */
  jobsList: () => Promise<BoardAgentActionMap['jobsList']['result']>;
  /** Owner only: what needs the owner on this runner (the attention badge). */
  attention: () => Promise<BoardAgentActionMap['attention']['result']>;
  /** Owner only: the runner's always-on service (start at login). */
  serviceStatus: () => Promise<BoardAgentActionMap['serviceStatus']['result']>;
  serviceInstall: () => Promise<
    BoardAgentActionMap['serviceInstall']['result']
  >;
  serviceRemove: () => Promise<BoardAgentActionMap['serviceRemove']['result']>;
  /** Owner only: the runner's parts, and one action on one part. */
  partsList: () => Promise<BoardAgentActionMap['partsList']['result']>;
  partsAct: (
    params: BoardAgentActionMap['partsAct']['payload']
  ) => Promise<BoardAgentActionMap['partsAct']['result']>;
  /** Owner only: Free up space, a dry run unless `confirm`. */
  machineCleanup: (
    params: BoardAgentActionMap['machineCleanup']['payload']
  ) => Promise<BoardAgentActionMap['machineCleanup']['result']>;
  /** The runner's Vault, runner-wide: how full, and who the owner let in. */
  vaultStatus: () => Promise<BoardAgentActionMap['vaultStatus']['result']>;
  vaultShareSet: (
    params: BoardAgentActionMap['vaultShareSet']['payload']
  ) => Promise<BoardAgentActionMap['vaultShareSet']['result']>;
  jobsCall: (
    params: BoardAgentActionMap['jobsCall']['payload']
  ) => Promise<BoardAgentActionMap['jobsCall']['result']>;
  jobStatus: (
    params: BoardAgentActionMap['jobStatus']['payload']
  ) => Promise<BoardAgentActionMap['jobStatus']['result']>;
  /** Live calls: every one for the owner, only your own for a member. */
  jobsActive: () => Promise<BoardAgentActionMap['jobsActive']['result']>;
  /** Only the caller who started it or the runner owner may cancel. */
  jobCancel: (
    params: BoardAgentActionMap['jobCancel']['payload']
  ) => Promise<BoardAgentActionMap['jobCancel']['result']>;
  /** Owner only. Newest first; `chainIntact` covers the whole file. */
  auditRead: (
    params: BoardAgentActionMap['auditRead']['payload']
  ) => Promise<BoardAgentActionMap['auditRead']['result']>;
  auditClear: () => Promise<BoardAgentActionMap['auditClear']['result']>;
  keepAwakeSet: (
    payload: BoardAgentActionMap['keepAwakeSet']['payload']
  ) => Promise<BoardAgentActionMap['keepAwakeSet']['result']>;
  secretsInboxList: () => Promise<
    BoardAgentActionMap['secretsInboxList']['result']
  >;
  /** Password goes runner-direct; it never enters the server. */
  secretsInboxAnswer: (
    params: BoardAgentActionMap['secretsInboxAnswer']['payload']
  ) => Promise<BoardAgentActionMap['secretsInboxAnswer']['result']>;
  deployRunDeny: () => Promise<BoardAgentActionMap['deployRunDeny']['result']>;
  deployRunCancel: () => Promise<
    BoardAgentActionMap['deployRunCancel']['result']
  >;
  deployRunDismiss: () => Promise<
    BoardAgentActionMap['deployRunDismiss']['result']
  >;
  onDeployRun: (
    callback: (data: BoardAgentEventMap['deploy-run']) => void
  ) => () => void;
  /** Creator settled (drafted/failed) - refetch deployCreatorState on it. */
  onDeployCreator: (
    callback: (data: BoardAgentEventMap['deploy-creator']) => void
  ) => () => void;
  deployApproveSkill: () => Promise<
    BoardAgentActionMap['deployApproveSkill']['result']
  >;
  deployDiscardSkill: () => Promise<
    BoardAgentActionMap['deployDiscardSkill']['result']
  >;
  /** Secrets store: key names + set-status only; values are write-only.
   *  No caching - every call carries the password. */
  deploySecretsStatus: () => Promise<
    BoardAgentActionMap['deploySecretsStatus']['result']
  >;
  deploySecretsInit: (
    params: BoardAgentActionMap['deploySecretsInit']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsInit']['result']>;
  deploySecretsList: (
    params: BoardAgentActionMap['deploySecretsList']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsList']['result']>;
  deploySecretsSet: (
    params: BoardAgentActionMap['deploySecretsSet']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsSet']['result']>;
  deploySecretsSetMeta: (
    params: BoardAgentActionMap['deploySecretsSetMeta']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsSetMeta']['result']>;
  deploySecretsUnset: (
    params: BoardAgentActionMap['deploySecretsUnset']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsUnset']['result']>;
  /** File secrets (sops blobs): a .p12, a .p8, an SSH key. Owner only. */
  deploySecretsSetFile: (
    params: BoardAgentActionMap['deploySecretsSetFile']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsSetFile']['result']>;
  deploySecretsUnsetFile: (
    params: BoardAgentActionMap['deploySecretsUnsetFile']['payload']
  ) => Promise<BoardAgentActionMap['deploySecretsUnsetFile']['result']>;
  /** One key inside an env file secret (a server env). Owner only. */
  secretFileEnvKeys: (
    params: BoardAgentActionMap['secretFileEnvKeys']['payload']
  ) => Promise<BoardAgentActionMap['secretFileEnvKeys']['result']>;
  secretFileEnvSet: (
    params: BoardAgentActionMap['secretFileEnvSet']['payload']
  ) => Promise<BoardAgentActionMap['secretFileEnvSet']['result']>;
  secretFileEnvUnset: (
    params: BoardAgentActionMap['secretFileEnvUnset']['payload']
  ) => Promise<BoardAgentActionMap['secretFileEnvUnset']['result']>;
  setupStatus: () => Promise<BoardAgentActionMap['setupStatus']['result']>;
  /** Throws "Unknown board-agent action" on a runner that predates it. */
  environmentStatus: () => Promise<
    BoardAgentActionMap['environmentStatus']['result']
  >;
  environmentInstall: (
    params: BoardAgentActionMap['environmentInstall']['payload']
  ) => Promise<BoardAgentActionMap['environmentInstall']['result']>;
  ensureSetup: () => Promise<BoardAgentActionMap['ensureSetup']['result']>;
  cardExecute: (
    params: BoardAgentActionMap['cardExecute']['payload']
  ) => Promise<BoardAgentActionMap['cardExecute']['result']>;
  cardMergeContent: (
    params: BoardAgentActionMap['cardMergeContent']['payload']
  ) => Promise<BoardAgentActionMap['cardMergeContent']['result']>;
  cardMergeSessions: (
    params: BoardAgentActionMap['cardMergeSessions']['payload']
  ) => Promise<BoardAgentActionMap['cardMergeSessions']['result']>;
  cardStartPreview: (
    postId: string
  ) => Promise<BoardAgentActionMap['cardStartPreview']['result']>;
  cardSessions: () => Promise<BoardAgentActionMap['cardSessions']['result']>;
  cardSession: (postId: string) => Promise<CardAISessionFull | null>;
  cardDismiss: (postId: string) => Promise<void>;
  cardRemoteBranches: () => Promise<
    BoardAgentActionMap['cardRemoteBranches']['result']
  >;
  bareWorkNames: () => Promise<BoardAgentActionMap['bareWorkNames']['result']>;
  onedriveStartDeviceCode: (
    params: BoardAgentActionMap['onedriveStartDeviceCode']['payload']
  ) => Promise<BoardAgentActionMap['onedriveStartDeviceCode']['result']>;
  /** GitHub click-to-connect (device flow). Token stays on the runner, until
   *  a disconnect sends it to the server to be revoked at GitHub. */
  githubStartDeviceCode: (
    params: BoardAgentActionMap['githubStartDeviceCode']['payload']
  ) => Promise<BoardAgentActionMap['githubStartDeviceCode']['result']>;
  githubDisconnect: () => Promise<
    BoardAgentActionMap['githubDisconnect']['result']
  >;
  githubIdentity: () => Promise<
    BoardAgentActionMap['githubIdentity']['result']
  >;
  githubListRepos: () => Promise<
    BoardAgentActionMap['githubListRepos']['result']
  >;
  githubListBranches: (
    params: BoardAgentActionMap['githubListBranches']['payload']
  ) => Promise<BoardAgentActionMap['githubListBranches']['result']>;
  onGithubAuth: (
    callback: (data: BoardAgentEventMap['github-auth']) => void
  ) => () => void;
  onedriveDisconnect: () => Promise<
    BoardAgentActionMap['onedriveDisconnect']['result']
  >;
  /** Host pushes here when the device-code flow finishes (no client polling). */
  onOneDriveAuth: (
    callback: (data: BoardAgentEventMap['onedrive-auth']) => void
  ) => () => void;
  // One-click Claude sign-in: start returns the OAuth URL to open, the pasted
  // code goes back via claudeLoginCode, the outcome lands on onClaudeAuth.
  claudeLoginStart: () => Promise<
    BoardAgentActionMap['claudeLoginStart']['result']
  >;
  claudeLoginCode: (
    code: string
  ) => Promise<BoardAgentActionMap['claudeLoginCode']['result']>;
  claudeLoginCancel: () => Promise<
    BoardAgentActionMap['claudeLoginCancel']['result']
  >;
  claudeLogout: () => Promise<BoardAgentActionMap['claudeLogout']['result']>;
  onClaudeAuth: (
    callback: (data: BoardAgentEventMap['claude-auth']) => void
  ) => () => void;
  // One-click Codex sign-in: start returns the verification URL + one-time
  // code to show; the CLI polls OpenAI itself; outcome lands on onCodexAuth.
  codexLoginStart: () => Promise<
    BoardAgentActionMap['codexLoginStart']['result']
  >;
  codexLoginCancel: () => Promise<
    BoardAgentActionMap['codexLoginCancel']['result']
  >;
  codexLogout: () => Promise<BoardAgentActionMap['codexLogout']['result']>;
  // Close a sign-in or failed-Ask row (activityDismissable).
  activityDismiss: (
    id: string
  ) => Promise<BoardAgentActionMap['activityDismiss']['result']>;
  onCodexAuth: (
    callback: (data: BoardAgentEventMap['codex-auth']) => void
  ) => () => void;
  /** Host pushes Spotlight-style OneDrive index progress as a pass runs. */
  onOneDriveIndex: (
    callback: (data: BoardAgentEventMap['onedrive-index']) => void
  ) => () => void;
  /** Current OneDrive index status, queried on demand (open/reconnect). */
  onedriveIndexStatus: () => Promise<
    BoardAgentActionMap['onedriveIndexStatus']['result']
  >;
  /** Force a full re-walk of the OneDrive index. */
  onedriveReindex: () => Promise<
    BoardAgentActionMap['onedriveReindex']['result']
  >;
  /** One level of the drive for the folder picker; no id browses the root. */
  onedriveBrowseFolders: (
    params: BoardAgentActionMap['onedriveBrowseFolders']['payload']
  ) => Promise<BoardAgentActionMap['onedriveBrowseFolders']['result']>;
  /** Start indexing a folder. It stays in sync from then on. */
  onedriveSelectFolder: (
    params: BoardAgentActionMap['onedriveSelectFolder']['payload']
  ) => Promise<BoardAgentActionMap['onedriveSelectFolder']['result']>;
  /** Stop indexing a folder and delete everything indexed under it. */
  onedriveUnselectFolder: (
    params: BoardAgentActionMap['onedriveUnselectFolder']['payload']
  ) => Promise<BoardAgentActionMap['onedriveUnselectFolder']['result']>;
  cardFinalize: (
    params: BoardAgentActionMap['cardFinalize']['payload']
  ) => Promise<BoardAgentActionMap['cardFinalize']['result']>;
  cardAnswerQuestion: (postId: string, answer: string) => Promise<void>;
  cardFeedback: (postId: string, feedback: string) => Promise<void>;
  cardKill: (postId: string) => Promise<void>;
  /** Stop a card's headless agent, keeping its worktree + conversation so a
   *  terminal can resume the exact thread (terminal takeover). */
  cardHandoff: (postId: string) => Promise<void>;

  /** Bare mode file ops. Paths are relative to the repo root. */
  bareFsList: (
    params: BoardAgentActionMap['bareFsList']['payload']
  ) => Promise<BoardAgentActionMap['bareFsList']['result']>;
  bareFsSearch: (
    params: BoardAgentActionMap['bareFsSearch']['payload']
  ) => Promise<BoardAgentActionMap['bareFsSearch']['result']>;
  bareFsRead: (
    params: BoardAgentActionMap['bareFsRead']['payload']
  ) => Promise<BoardAgentActionMap['bareFsRead']['result']>;
  bareFsWrite: (
    params: BoardAgentActionMap['bareFsWrite']['payload']
  ) => Promise<void>;
  bareFsDelete: (
    params: BoardAgentActionMap['bareFsDelete']['payload']
  ) => Promise<void>;
  bareFsMkdir: (
    params: BoardAgentActionMap['bareFsMkdir']['payload']
  ) => Promise<void>;
  terminalDropImage: (
    params: BoardAgentActionMap['terminalDropImage']['payload']
  ) => Promise<BoardAgentActionMap['terminalDropImage']['result']>;
  terminalDropFile: (
    params: BoardAgentActionMap['terminalDropFile']['payload']
  ) => Promise<BoardAgentActionMap['terminalDropFile']['result']>;
  bareReview: (
    params: BoardAgentActionMap['bareReview']['payload']
  ) => Promise<BoardAgentActionMap['bareReview']['result']>;
  bareGitStatus: (
    params: BoardAgentActionMap['bareGitStatus']['payload']
  ) => Promise<BoardAgentActionMap['bareGitStatus']['result']>;
  bareGitCommitDiff: (
    params: BoardAgentActionMap['bareGitCommitDiff']['payload']
  ) => Promise<BoardAgentActionMap['bareGitCommitDiff']['result']>;
  bareGitCompare: (
    params: BoardAgentActionMap['bareGitCompare']['payload']
  ) => Promise<BoardAgentActionMap['bareGitCompare']['result']>;
  bareGitStage: (
    params: BoardAgentActionMap['bareGitStage']['payload']
  ) => Promise<BoardAgentActionMap['bareGitStage']['result']>;
  bareGitUnstage: (
    params: BoardAgentActionMap['bareGitUnstage']['payload']
  ) => Promise<BoardAgentActionMap['bareGitUnstage']['result']>;
  bareListTerminals: (
    params: BoardAgentActionMap['bareListTerminals']['payload']
  ) => Promise<BoardAgentActionMap['bareListTerminals']['result']>;
  bareTerminalHealth: (
    params: BoardAgentActionMap['bareTerminalHealth']['payload']
  ) => Promise<BoardAgentActionMap['bareTerminalHealth']['result']>;
  bareAckTerminalDigest: () => Promise<
    BoardAgentActionMap['bareAckTerminalDigest']['result']
  >;
  bareRestartStaleTerminals: (
    params: BoardAgentActionMap['bareRestartStaleTerminals']['payload']
  ) => Promise<BoardAgentActionMap['bareRestartStaleTerminals']['result']>;
  bareListProcesses: (
    params: BoardAgentActionMap['bareListProcesses']['payload']
  ) => Promise<BoardAgentActionMap['bareListProcesses']['result']>;
  bareKillProcess: (
    params: BoardAgentActionMap['bareKillProcess']['payload']
  ) => Promise<void>;
  bareEditorState: (
    params: BoardAgentActionMap['bareEditorState']['payload']
  ) => Promise<BoardAgentActionMap['bareEditorState']['result']>;
  bareOpenFile: (
    params: BoardAgentActionMap['bareOpenFile']['payload']
  ) => Promise<void>;
  bareCloseFile: (
    params: BoardAgentActionMap['bareCloseFile']['payload']
  ) => Promise<void>;
  bareBufferSet: (
    params: BoardAgentActionMap['bareBufferSet']['payload']
  ) => Promise<void>;
  bareExpose: (
    params: BoardAgentActionMap['bareExpose']['payload']
  ) => Promise<BoardAgentActionMap['bareExpose']['result']>;
  bareUnexpose: (
    params: BoardAgentActionMap['bareUnexpose']['payload']
  ) => Promise<void>;
  bareListExposed: (
    params: BoardAgentActionMap['bareListExposed']['payload']
  ) => Promise<BoardAgentActionMap['bareListExposed']['result']>;

  /** The space's scratchpad: what an agent has drawn for the user. Space-wide,
   *  so no postId - see .specs/scratchpad.md. */
  scratchpadList: () => Promise<
    BoardAgentActionMap['scratchpadList']['result']
  >;
  /** One item's bytes, base64. Rejects anything past SCRATCHPAD_MAX_BYTES. */
  scratchpadRead: (
    params: BoardAgentActionMap['scratchpadRead']['payload']
  ) => Promise<BoardAgentActionMap['scratchpadRead']['result']>;
  /** One byte range, for downloading an item too big to preview. */
  scratchpadReadChunk: (
    params: BoardAgentActionMap['scratchpadReadChunk']['payload']
  ) => Promise<BoardAgentActionMap['scratchpadReadChunk']['result']>;
  scratchpadDelete: (
    params: BoardAgentActionMap['scratchpadDelete']['payload']
  ) => Promise<void>;
  scratchpadClear: () => Promise<void>;

  /** How the host is protecting this space's credentials, and the remedy when
   *  it can't. Owner-only on a runner; every call below is too. */
  secureStatus: () => Promise<BoardAgentActionMap['secureStatus']['result']>;
  /** Give a runner its old connection password once: it converts the files
   *  locked behind it and removes the password. */
  secureUnlock: (
    params: BoardAgentActionMap['secureUnlock']['payload']
  ) => Promise<BoardAgentActionMap['secureUnlock']['result']>;
  /** Fires whenever the runner's credential-store state changes, so a panel
   *  open on another device follows an unlock done elsewhere. */
  onSecureState: (
    callback: (status: BoardAgentActionMap['secureStatus']['result']) => void
  ) => () => void;

  /**
   * Open an interactive shell into the card's container. Used by Bare mode.
   * Returns a handle that streams stdout via onData and accepts keystrokes
   * via write.
   */
  terminal: OpenTerminal;
  killTerminal: KillTerminal;
  restartTerminal: RestartTerminal;

  onBoardLog: (callback: (entry: CardAILogEntry) => void) => () => void;
  onStatus: (callback: (msg: string) => void) => () => void;
  onSetupStatus: (callback: (msg: string) => void) => () => void;
  onSessionUpdate: (
    callback: (data: CardAISessionSummary) => void
  ) => () => void;
  /** Space-level work that isn't a card session: Ask Board, deploys, setup,
   *  indexing, sign-ins. `state: null` retires the row. */
  onActivityUpdate: (callback: (data: RunnerActivity) => void) => () => void;
  onLog: (
    postId: string,
    callback: (entry: CardAILogEntry) => void
  ) => () => void;
  /** Working-tree changes for one bare card, made by the agent, the terminal
   *  or git rather than by this editor. */
  onBareFsChange: (
    postId: string,
    callback: (change: BareFsChange) => void
  ) => () => void;
  /** The scratchpad directory changed. Carries no paths: the panel refetches
   *  the listing, which is one small call. */
  onScratchpad: (callback: (payload: { at: number }) => void) => () => void;
  /** The store's keys changed, from any client, the CLI or an import: the
   *  attention badge asks again. */
  onSecretsChanged: (
    callback: (payload: { at: number }) => void
  ) => () => void;

  simulatorList: () => Promise<SimulatorDevice[]>;
  simulatorStatus: () => Promise<{ booted: string[] }>;
  simulatorAttach: (
    payload: BoardAgentActionMap['simulatorAttach']['payload']
  ) => Promise<void>;
  simulatorDetach: () => Promise<void>;
  simulatorInput: (payload: SimulatorInput) => Promise<void>;
  screenStatus: (
    params: BoardAgentActionMap['screenStatus']['payload']
  ) => Promise<BoardAgentActionMap['screenStatus']['result']>;
  screenAttach: (
    params: BoardAgentActionMap['screenAttach']['payload']
  ) => Promise<BoardAgentActionMap['screenAttach']['result']>;
  screenDetach: () => Promise<void>;
  screenInput: (
    params: BoardAgentActionMap['screenInput']['payload']
  ) => Promise<void>;

  // Multi-device sync
  broadcast: (payload: BoardUpdatePayload) => void;
  onBoardUpdate: (
    callback: (payload: BoardUpdatePayload) => void
  ) => () => void;
};

// Long-running actions that should never time out
const NO_TIMEOUT_ACTIONS = new Set([
  'ensureSetup',
  // Homebrew's own install, or a cask, can outlast the cap on a slow Mac.
  'environmentInstall',
  'cardExecute',
  'cardMergeContent',
  'cardMergeSessions',
  'cardFinalize',
  'cardStartPreview',
  'ask',
  'deployCreateSkill',
  'deployReviseSkill',
  'deployFixEnvironment',
  // Clones or fetches the workspace, then pushes a branch: network-bound on a
  // cold space, so the 300s cap could fire on the first proposal.
  'skillProposeRepo',
  'skillMigrate',
  'skillsList'
]);

const REQUEST_TIMEOUT_MS = 300_000;

/**
 * Wraps a WebRTC data channel in a typed request/response RPC surface that
 * mirrors the electron board-agent IPC. Used by the client + mobile hooks.
 */
export function buildRemoteBoardAgent(
  spaceSlug: string,
  channel: DataChannelInterface,
  // Stable board id, sent alongside the slug so the runner names containers
  // durably. Optional - older callers send only the slug.
  spaceId?: number
): BoardAgentAPI {
  let requestCounter = 0;
  const pendingRequests = new Map<
    string,
    { resolve: (value: unknown) => void; reject: (err: Error) => void }
  >();
  const eventListeners = new Map<string, Set<(data: unknown) => void>>();

  type TerminalListeners = {
    data: Set<(chunk: Uint8Array) => void>;
    base64: Set<(b64: string) => void>;
    exit: Set<(info: { code: number | null; reason?: string }) => void>;
  };
  const terminals = new Map<string, TerminalListeners>();

  // A dead channel can never deliver a result. Reject everything in flight so
  // the caller shows an error instead of spinning - the long deploy/card
  // actions carry no timeout, so this close is their only settle signal. The
  // runner keeps working; the UI reattaches to its state on reconnect.
  channel.onClose?.(() => {
    if (pendingRequests.size === 0) return;
    const stranded = [...pendingRequests.entries()];
    pendingRequests.clear();
    for (const [, pending] of stranded) {
      pending.reject(
        new Error(
          'Lost the connection to the runner. It keeps working - this reattaches when the connection returns.'
        )
      );
    }
  });

  const reassembler = makeChunkReassembler();
  channel.onMessage((raw) => {
    const wire = reassembler.feed(raw);
    if (wire === null) return; // more chunks pending
    try {
      const msg = JSON.parse(wire);

      if (msg.type === 'result' && msg.requestId) {
        const pending = pendingRequests.get(msg.requestId);
        if (pending) {
          pendingRequests.delete(msg.requestId);
          if (msg.error) pending.reject(new Error(msg.error));
          else pending.resolve(msg.result);
        }
      }

      if (msg.type === 'event' && msg.event) {
        const listeners = eventListeners.get(msg.event);
        if (listeners) {
          for (const listener of listeners) listener(msg.payload);
        }
      }

      // Runner-wide, not board-scoped: the credential store spans every board,
      // so it rides its own frame and fans out to the reserved event key.
      if (msg.type === 'runner-secure') {
        const listeners = eventListeners.get('runner-secure');
        if (listeners) {
          for (const listener of listeners) listener(msg.status);
        }
      }

      if (msg.type === 'terminal') {
        routeTerminalFrame(msg as TerminalServerMessage);
      }
    } catch {
      // malformed message
    }
  });

  function routeTerminalFrame(msg: TerminalServerMessage): void {
    const handle = terminals.get(msg.sessionId);
    if (!handle) return;
    if (msg.op === 'stdout') {
      for (const cb of handle.base64) cb(msg.data);
      // Decoded only when someone wants bytes (web's xterm): unchanged there.
      if (handle.data.size > 0) {
        const bytes = base64ToBytes(msg.data);
        for (const cb of handle.data) cb(bytes);
      }
      return;
    }
    if (msg.op === 'exit') {
      for (const cb of handle.exit) cb({ code: msg.code, reason: msg.reason });
      terminals.delete(msg.sessionId);
      return;
    }
    if (msg.op === 'error') {
      for (const cb of handle.exit) cb({ code: null, reason: msg.message });
      terminals.delete(msg.sessionId);
    }
    // 'opened' - no client-side action needed
  }

  function dispatch<T>(action: string, payload?: unknown): Promise<T> {
    const requestId = `req-${++requestCounter}-${Date.now()}`;
    const answered = new Promise<T>((resolve, reject) => {
      pendingRequests.set(requestId, {
        resolve: resolve as (value: unknown) => void,
        reject
      });

      const wire = JSON.stringify({
        type: 'dispatch',
        action,
        spaceSlug,
        spaceId,
        payload: payload ?? null,
        requestId
      });
      for (const frame of chunkWire(wire)) channel.send(frame);

      if (!NO_TIMEOUT_ACTIONS.has(action)) {
        setTimeout(() => {
          if (pendingRequests.has(requestId)) {
            pendingRequests.delete(requestId);
            reject(new Error(`Request timed out: ${action}`));
          }
        }, REQUEST_TIMEOUT_MS);
      }
    });
    // A secrets write changes what the runner badge counts as missing.
    return answered.then((result) => {
      noteRunnerAction(action);
      return result;
    });
  }

  function sendWire(obj: unknown): void {
    const wire = JSON.stringify(obj);
    for (const frame of chunkWire(wire)) channel.send(frame);
  }

  let terminalSessionCounter = 0;
  const openTerminal: OpenTerminal = ({
    postId,
    cols,
    rows,
    terminalKey,
    resumeAgentSessionId,
    resumeProvider,
    briefCard
  }) => {
    const sessionId = `terminal-${++terminalSessionCounter}-${Date.now()}`;
    const listeners: TerminalListeners = {
      data: new Set(),
      base64: new Set(),
      exit: new Set()
    };
    terminals.set(sessionId, listeners);

    sendWire({
      type: 'terminal',
      op: 'open',
      sessionId,
      postId,
      cols,
      rows,
      terminalKey,
      resumeAgentSessionId,
      resumeProvider,
      briefCard
    });

    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      sendWire({ type: 'terminal', op: 'close', sessionId });
      terminals.delete(sessionId);
    };

    return {
      write: (data) => {
        if (closed) return;
        sendWire({
          type: 'terminal',
          op: 'stdin',
          sessionId,
          data: textToBase64(data)
        });
      },
      onData: (cb) => {
        listeners.data.add(cb);
        return () => listeners.data.delete(cb);
      },
      onBase64: (cb) => {
        listeners.base64.add(cb);
        return () => listeners.base64.delete(cb);
      },
      onExit: (cb) => {
        listeners.exit.add(cb);
        return () => listeners.exit.delete(cb);
      },
      resize: (cols, rows) => {
        if (closed) return;
        sendWire({ type: 'terminal', op: 'resize', sessionId, cols, rows });
      },
      close
    };
  };

  function on<T>(event: string, callback: (data: T) => void): () => void {
    const channelKey = `board-agent:${spaceSlug}:${event}`;
    if (!eventListeners.has(channelKey)) {
      eventListeners.set(channelKey, new Set());
    }
    const wrapped = callback as (data: unknown) => void;
    eventListeners.get(channelKey)!.add(wrapped);
    return () => {
      eventListeners.get(channelKey)?.delete(wrapped);
    };
  }

  return {
    getConfig: () => dispatch('getConfig'),
    setConfig: (config) => dispatch('setConfig', config),
    clearToken: (key) => dispatch('clearToken', { key }),

    askBoard: (params) => dispatch('ask', params),
    resetBoardSession: () => dispatch('resetBoardSession'),
    boardSession: (conversationId) =>
      dispatch('boardSession', { conversationId }),
    boardSessions: () => dispatch('boardSessions'),
    boardSessionUpdate: (params) => dispatch('boardSessionUpdate', params),
    boardSessionDelete: (conversationId) =>
      dispatch('boardSessionDelete', { conversationId }),
    boardHandoff: (conversationId) =>
      dispatch('boardHandoff', { conversationId }),

    skillsList: () => dispatch('skillsList'),
    skillRead: (params) => dispatch('skillRead', params),
    skillWriteGlobal: (params) => dispatch('skillWriteGlobal', params),
    skillDeleteGlobal: (params) => dispatch('skillDeleteGlobal', params),
    skillProposeRepo: (params) => dispatch('skillProposeRepo', params),
    skillMigrate: (params) => dispatch('skillMigrate', params),

    deployStatus: () => dispatch('deployStatus'),
    deployBranches: () => dispatch('deployBranches'),
    deployCreateSkill: (params) => dispatch('deployCreateSkill', params),
    deployReviseSkill: (params) => dispatch('deployReviseSkill', params),
    deployCreatorState: () => dispatch('deployCreatorState'),
    deployLoadSkillBranch: (params) =>
      dispatch('deployLoadSkillBranch', params),
    deployCheckEnvironment: () => dispatch('deployCheckEnvironment'),
    deployFixEnvironment: (params) => dispatch('deployFixEnvironment', params),
    deployEnvState: () => dispatch('deployEnvState'),
    onDeployEnv: (callback) => on('deploy-env', callback),
    deployRunStart: (params) => dispatch('deployRunStart', params),
    deployRunState: () => dispatch('deployRunState'),
    deployRunAnswer: (params) => dispatch('deployRunAnswer', params),
    setupAuthorize: (params) => dispatch('setupAuthorize', params),
    deployRunAuthorize: (params) => dispatch('deployRunAuthorize', params),
    secretsAuthState: () => dispatch('secretsAuthState'),
    secretsAuthAnswer: (params) => dispatch('secretsAuthAnswer', params),
    onSecretsAuth: (callback) => on('secrets-auth', callback),
    dangerousApprovalState: () => dispatch('dangerousApprovalState'),
    dangerousApprovalAnswer: (params) =>
      dispatch('dangerousApprovalAnswer', params),
    secretsLock: () => dispatch('secretsLock', {}),
    secretsExportBackup: (params) => dispatch('secretsExportBackup', params),
    secretsOpenBackup: (params) => dispatch('secretsOpenBackup', params),
    secretsImportBackupPlan: (params) =>
      dispatch('secretsImportBackupPlan', params),
    secretsImportBackupApply: (params) =>
      dispatch('secretsImportBackupApply', params),
    secretsChangePassword: (params) =>
      dispatch('secretsChangePassword', params),
    runnerAccessGet: () => dispatch('runnerAccessGet'),
    runnerAccessSet: (params) => dispatch('runnerAccessSet', params),
    onRunnerAccess: (callback) => on('runner-access', callback),
    jobsList: () => dispatch('jobsList'),
    attention: () => dispatch('attention'),
    serviceStatus: () => dispatch('serviceStatus'),
    serviceInstall: () => dispatch('serviceInstall'),
    serviceRemove: () => dispatch('serviceRemove'),
    partsList: () => dispatch('partsList'),
    partsAct: (params) => dispatch('partsAct', params),
    machineCleanup: (params) => dispatch('machineCleanup', params),
    vaultStatus: () => dispatch('vaultStatus', {}),
    vaultShareSet: (params) => dispatch('vaultShareSet', params),
    jobsCall: (params) => dispatch('jobsCall', params),
    jobStatus: (params) => dispatch('jobStatus', params),
    jobCancel: (params) => dispatch('jobCancel', params),
    jobsActive: () => dispatch('jobsActive'),
    auditRead: (params) => dispatch('auditRead', params),
    auditClear: () => dispatch('auditClear', {}),
    keepAwakeSet: (payload) => dispatch('keepAwakeSet', payload),
    secretsInboxList: () => dispatch('secretsInboxList'),
    secretsInboxAnswer: (params) => dispatch('secretsInboxAnswer', params),
    deployRunDeny: () => dispatch('deployRunDeny'),
    deployRunCancel: () => dispatch('deployRunCancel'),
    deployRunDismiss: () => dispatch('deployRunDismiss'),
    onDeployRun: (callback) => on('deploy-run', callback),
    onDeployCreator: (callback) => on('deploy-creator', callback),
    deployApproveSkill: () => dispatch('deployApproveSkill'),
    deployDiscardSkill: () => dispatch('deployDiscardSkill'),
    deploySecretsStatus: () => dispatch('deploySecretsStatus'),
    deploySecretsInit: (params) => dispatch('deploySecretsInit', params),
    deploySecretsList: (params) => dispatch('deploySecretsList', params),
    deploySecretsSet: (params) => dispatch('deploySecretsSet', params),
    deploySecretsSetMeta: (params) => dispatch('deploySecretsSetMeta', params),
    deploySecretsUnset: (params) => dispatch('deploySecretsUnset', params),
    deploySecretsSetFile: (params) => dispatch('deploySecretsSetFile', params),
    deploySecretsUnsetFile: (params) =>
      dispatch('deploySecretsUnsetFile', params),
    secretFileEnvKeys: (params) => dispatch('secretFileEnvKeys', params),
    secretFileEnvSet: (params) => dispatch('secretFileEnvSet', params),
    secretFileEnvUnset: (params) => dispatch('secretFileEnvUnset', params),
    setupStatus: () => dispatch('setupStatus'),
    environmentStatus: () => dispatch('environmentStatus'),
    environmentInstall: (params) => dispatch('environmentInstall', params),
    ensureSetup: () => dispatch('ensureSetup'),
    cardExecute: (params) => dispatch('cardExecute', params),
    cardMergeContent: (params) => dispatch('cardMergeContent', params),
    cardMergeSessions: (params) => dispatch('cardMergeSessions', params),
    cardStartPreview: (postId) => dispatch('cardStartPreview', { postId }),
    cardSessions: () => dispatch('cardSessions'),
    cardSession: (postId) => dispatch('cardSession', { postId }),
    cardDismiss: (postId) => dispatch('cardDismiss', { postId }),
    cardRemoteBranches: () => dispatch('cardRemoteBranches'),
    bareWorkNames: () => dispatch('bareWorkNames'),
    onedriveStartDeviceCode: (params) =>
      dispatch('onedriveStartDeviceCode', params),
    onedriveDisconnect: () => dispatch('onedriveDisconnect'),
    githubStartDeviceCode: (params) =>
      dispatch('githubStartDeviceCode', params),
    githubDisconnect: () => dispatch('githubDisconnect'),
    githubIdentity: () => dispatch('githubIdentity'),
    githubListRepos: () => dispatch('githubListRepos'),
    githubListBranches: (params) => dispatch('githubListBranches', params),
    onGithubAuth: (callback) => on('github-auth', callback),
    onOneDriveAuth: (callback) => on('onedrive-auth', callback),
    claudeLoginStart: () => dispatch('claudeLoginStart'),
    claudeLoginCode: (code) => dispatch('claudeLoginCode', { code }),
    claudeLoginCancel: () => dispatch('claudeLoginCancel'),
    claudeLogout: () => dispatch('claudeLogout'),
    onClaudeAuth: (callback) => on('claude-auth', callback),
    codexLoginStart: () => dispatch('codexLoginStart'),
    codexLoginCancel: () => dispatch('codexLoginCancel'),
    codexLogout: () => dispatch('codexLogout'),
    activityDismiss: (id) => dispatch('activityDismiss', { id }),
    onCodexAuth: (callback) => on('codex-auth', callback),
    onOneDriveIndex: (callback) => on('onedrive-index', callback),
    onedriveIndexStatus: () => dispatch('onedriveIndexStatus'),
    onedriveReindex: () => dispatch('onedriveReindex'),
    onedriveBrowseFolders: (params) =>
      dispatch('onedriveBrowseFolders', params),
    onedriveSelectFolder: (params) => dispatch('onedriveSelectFolder', params),
    onedriveUnselectFolder: (params) =>
      dispatch('onedriveUnselectFolder', params),
    cardFinalize: (params) => dispatch('cardFinalize', params),
    cardAnswerQuestion: (postId, answer) =>
      dispatch('cardAnswerQuestion', { postId, answer }),
    cardFeedback: (postId, feedback) =>
      dispatch('cardFeedback', { postId, feedback }),
    cardKill: (postId) => dispatch('cardKill', { postId }),
    cardHandoff: (postId) => dispatch('cardHandoff', { postId }),

    bareFsList: (params) => dispatch('bareFsList', params),
    bareFsSearch: (params) => dispatch('bareFsSearch', params),
    bareFsRead: (params) => dispatch('bareFsRead', params),
    bareFsWrite: (params) => dispatch('bareFsWrite', params),
    bareFsDelete: (params) => dispatch('bareFsDelete', params),
    bareFsMkdir: (params) => dispatch('bareFsMkdir', params),
    terminalDropImage: (params) => dispatch('terminalDropImage', params),
    terminalDropFile: (params) => dispatch('terminalDropFile', params),
    bareReview: (params) => dispatch('bareReview', params),
    bareGitStatus: (params) => dispatch('bareGitStatus', params),
    bareGitCommitDiff: (params) => dispatch('bareGitCommitDiff', params),
    bareGitCompare: (params) => dispatch('bareGitCompare', params),
    bareGitStage: (params) => dispatch('bareGitStage', params),
    bareGitUnstage: (params) => dispatch('bareGitUnstage', params),
    bareListTerminals: (params) => dispatch('bareListTerminals', params),
    bareTerminalHealth: (params) => dispatch('bareTerminalHealth', params),
    bareAckTerminalDigest: () => dispatch('bareAckTerminalDigest', {}),
    bareRestartStaleTerminals: (params) =>
      dispatch('bareRestartStaleTerminals', params),
    bareListProcesses: (params) => dispatch('bareListProcesses', params),
    bareKillProcess: (params) => dispatch('bareKillProcess', params),
    bareEditorState: (params) => dispatch('bareEditorState', params),
    bareOpenFile: (params) => dispatch('bareOpenFile', params),
    bareCloseFile: (params) => dispatch('bareCloseFile', params),
    bareBufferSet: (params) => dispatch('bareBufferSet', params),
    bareExpose: (params) => dispatch('bareExpose', params),
    bareUnexpose: (params) => dispatch('bareUnexpose', params),
    bareListExposed: (params) => dispatch('bareListExposed', params),

    scratchpadList: () => dispatch('scratchpadList', {}),
    scratchpadRead: (params) => dispatch('scratchpadRead', params),
    scratchpadReadChunk: (params) => dispatch('scratchpadReadChunk', params),
    scratchpadDelete: (params) => dispatch('scratchpadDelete', params),
    scratchpadClear: () => dispatch('scratchpadClear', {}),

    secureStatus: () => dispatch('secureStatus'),
    secureUnlock: (params) => dispatch('secureUnlock', params),
    onSecureState: (callback) => on('runner-secure', callback),

    terminal: openTerminal,
    killTerminal: ({ postId, terminalKey }) =>
      sendWire({ type: 'terminal', op: 'kill', postId, terminalKey }),
    restartTerminal: ({ postId, terminalKey }) =>
      sendWire({ type: 'terminal', op: 'restart', postId, terminalKey }),

    onBoardLog: (callback) => on('log', callback),
    onStatus: (callback) => on('status', callback),
    onSetupStatus: (callback) => on('setup-status', callback),
    onSessionUpdate: (callback) => on('session-update', callback),
    onActivityUpdate: (callback) => on('activity-update', callback),
    onLog: (postId, callback) => on(`log:${postId}`, callback),
    onBareFsChange: (postId, callback) => on(`bare-fs:${postId}`, callback),
    simulatorList: () => dispatch('simulatorList'),
    simulatorStatus: () => dispatch('simulatorStatus'),
    simulatorAttach: (payload) => dispatch('simulatorAttach', payload),
    simulatorDetach: () => dispatch('simulatorDetach'),
    simulatorInput: (payload) => dispatch('simulatorInput', payload),
    screenStatus: (params) => dispatch('screenStatus', params),
    screenAttach: (params) => dispatch('screenAttach', params),
    screenDetach: () => dispatch('screenDetach'),
    screenInput: (payload) => dispatch('screenInput', payload),
    onScratchpad: (callback) => on('scratchpad', callback),
    onSecretsChanged: (callback) => on('secrets-changed', callback),

    broadcast: (payload) =>
      channel.send(
        JSON.stringify({
          type: 'broadcast',
          spaceSlug,
          event: 'board-update',
          payload
        })
      ),
    onBoardUpdate: (callback) => on('board-update', callback)
  };
}

// Base64 helpers that work in browser, RN, and node. Frames are small so we
// don't bother streaming - one frame per stdout chunk, already
// chunked to a few KB.
export function textToBase64(text: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(text, 'utf-8').toString('base64');
  }
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array {
  if (typeof Buffer !== 'undefined') {
    const buf = Buffer.from(b64, 'base64');
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
